import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowRight, Loader2, RotateCcw, ChevronDown, ChevronRight, ShieldAlert } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ShowcasePage() {
  const [token, setToken] = useState(() => localStorage.getItem('futurepath_token') || '');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [open, setOpen] = useState({});
  const [creds, setCreds] = useState({ username: '', password: '' });

  const load = useCallback(async (t) => {
    if (!t) { setLoading(false); return; }
    try {
      const res = await axios.get(`${API}/labs/showcase`, { headers: { Authorization: `Bearer ${t}` } });
      setData(res.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(token); }, [load, token]);

  const signIn = async () => {
    setBusy('login');
    try {
      const res = await axios.post(`${API}/labs/login`, {
        username: creds.username.trim(), password: creds.password,
      });
      localStorage.setItem('futurepath_token', res.data.access_token);
      setToken(res.data.access_token);
      setLoading(true);
    } catch {
      toast.error('Invalid username or password.');
    } finally {
      setBusy('');
    }
  };

  const enter = async (key) => {
    setBusy(key);
    try {
      const res = await axios.post(`${API}/labs/showcase/enter`, { key },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 60000 });
      localStorage.setItem('futurepath_token', res.data.access_token);
      localStorage.setItem('futurepath_showcase_presenter', token);
      window.location.href = '/app/dashboard';
    } catch {
      toast.error('Could not open that demo profile.');
      setBusy('');
    }
  };

  const reset = async (key) => {
    setBusy(`reset-${key || 'all'}`);
    try {
      await axios.post(`${API}/labs/showcase/reset`, key ? { key } : {},
        { headers: { Authorization: `Bearer ${token}` }, timeout: 120000 });
      toast.success(key ? 'Profile reset to its original state' : 'All four profiles reset');
    } catch {
      toast.error('Reset failed.');
    } finally {
      setBusy('');
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-navy-gradient flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-cyan-300" /></div>;
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-navy-gradient flex items-center justify-center p-4">
        <div className="glass-card p-6 w-full max-w-sm" data-testid="showcase-login">
          <h1 className="font-outfit text-2xl font-bold text-white text-center">Master Platform Demo</h1>
          <p className="text-sm text-slate-300 text-center mt-1 mb-5">Presenter sign-in</p>
          <input className="w-full px-4 py-3 rounded-xl bg-slate-900/70 border border-slate-700 text-white placeholder-slate-500 mb-3"
            placeholder="Username" value={creds.username} data-testid="showcase-username"
            onChange={e => setCreds({ ...creds, username: e.target.value })} />
          <input type="password" className="w-full px-4 py-3 rounded-xl bg-slate-900/70 border border-slate-700 text-white placeholder-slate-500"
            placeholder="Password" value={creds.password} data-testid="showcase-password"
            onKeyDown={e => e.key === 'Enter' && signIn()}
            onChange={e => setCreds({ ...creds, password: e.target.value })} />
          <button onClick={signIn} disabled={busy === 'login'} data-testid="showcase-signin"
            className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold disabled:opacity-60">
            {busy === 'login' ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Continue <ArrowRight className="w-4 h-4" /></>}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-navy-gradient p-4 sm:p-8" data-testid="showcase-page">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-3 mb-2">
          <img src="/fp-logo.png" alt="Agentic FuturePath AI" className="w-10 h-10 rounded-xl" />
          <h1 className="font-outfit text-3xl sm:text-4xl font-bold text-white tracking-tight">{data.title}</h1>
        </div>
        <p className="text-base md:text-lg text-cyan-200">{data.subtitle}</p>
        <p className="text-sm text-slate-300 mt-2 max-w-3xl">{data.blurb}</p>

        <div className="flex flex-wrap items-center gap-3 mt-4 mb-8">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] uppercase tracking-wider bg-amber-500/10 border border-amber-500/30 text-amber-200">
            <ShieldAlert className="w-3.5 h-3.5" /> {data.data_notice}
          </span>
          {data.is_presenter && (
            <button onClick={() => reset(null)} disabled={!!busy} data-testid="reset-all-btn"
              className="text-xs text-slate-300 hover:text-white inline-flex items-center gap-1.5">
              <RotateCcw className="w-3.5 h-3.5" /> Reset all four profiles
            </button>
          )}
        </div>

        <div className="grid sm:grid-cols-2 gap-5">
          {data.profiles.map(p => (
            <div key={p.key} className={`rounded-2xl border p-5 transition-colors ${p.active ? 'border-cyan-400/60 bg-cyan-500/10' : 'border-slate-700 bg-slate-900/50 hover:border-slate-500'}`}
              data-testid={`showcase-card-${p.key}`}>
              <div className="flex items-start gap-4">
                <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${p.avatar_gradient} flex items-center justify-center text-white font-outfit font-bold text-xl shrink-0`}>
                  {p.name.split(' ').map(n => n[0]).join('')}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-outfit text-lg font-bold text-white leading-tight">{p.name} <span className="text-slate-300 font-normal text-sm">· {p.age}</span></p>
                  <p className="text-[11px] uppercase tracking-wider text-cyan-300 mt-0.5">{p.archetype_label}</p>
                  <p className="text-xs text-slate-300 mt-2">{p.situation}</p>
                </div>
              </div>

              <p className="text-sm text-white font-medium mt-4">{p.headline}</p>
              <p className="text-xs text-slate-300 mt-1.5 leading-relaxed">{p.description}</p>
              <p className="text-xs text-slate-300 mt-2">Objective: {p.objective} · {p.skill_count} skills on file · target: {p.target_career}</p>

              <div className="flex flex-wrap items-center gap-3 mt-4">
                <button onClick={() => enter(p.key)} disabled={!!busy} data-testid={`enter-demo-${p.key}`}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 text-white text-sm font-semibold active:scale-95 disabled:opacity-60">
                  {busy === p.key ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Enter Demo <ArrowRight className="w-4 h-4" /></>}
                </button>
                <button onClick={() => reset(p.key)} disabled={!!busy} data-testid={`reset-${p.key}`}
                  className={`text-xs text-slate-300 hover:text-white inline-flex items-center gap-1.5 ${data.is_presenter ? '' : 'hidden'}`}>
                  <RotateCcw className="w-3.5 h-3.5" /> Reset
                </button>
              </div>

              {p.story && (
                <div className="mt-4 border-t border-slate-700 pt-3">
                  <button onClick={() => setOpen(o => ({ ...o, [p.key]: !o[p.key] }))}
                    data-testid={`story-toggle-${p.key}`}
                    className="w-full flex items-center justify-between text-xs text-slate-300 hover:text-white">
                    <span className="uppercase tracking-wider">Presenter notes</span>
                    {open[p.key] ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                  </button>
                  {open[p.key] && (
                    <div className="mt-2 space-y-1.5 text-xs" data-testid={`story-${p.key}`}>
                      <p className="text-slate-300"><span className="text-rose-300">Problem:</span> {p.story.problem}</p>
                      <p className="text-slate-300"><span className="text-cyan-300">Show:</span> {p.story.show}</p>
                      <p className="text-slate-300"><span className="text-emerald-300">Takeaway:</span> {p.story.takeaway}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
